import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';
import * as XLSX from 'xlsx-js-style';
// @ts-ignore
import Plotly from 'plotly.js-dist-min';
import ThraceMapPanel from '../components/Thrace/ThraceMapPanel';
import 'leaflet/dist/leaflet.css';

interface Template {
  id: string;
  name: string;
  fileName: string;
}

interface ErrorRow {
  rowId: number;
  village: string;
  country: string;
  date: string;
  error: string;
}

interface CycleReportData {
  population: any[];
  clinical: any[];
  serology: any[];
  population_inventory_year?: number | null;
  population_inventory_is_fallback?: boolean;
  outbreak?: {
    has_outbreak?: boolean;
    months?: string[];
    by_disease?: Record<string, { label: string; source: string }[]>;
    note?: string | null;
  } | null;
}

/** Shared cycle-report columns — UI table and Excel must stay in sync. */
type CycleCol = { key: string; header: string; numeric?: boolean; naIfNull?: boolean };

const CYCLE_POP_COLS: CycleCol[] = [
  { key: 'country', header: 'Country' },
  { key: 'district_province', header: 'Province/District' },
  { key: 'cattle_pop', header: 'Cattle Pop.', numeric: true, naIfNull: true },
  { key: 'sheep_pop', header: 'Sheep Pop.', numeric: true, naIfNull: true },
  { key: 'goat_pop', header: 'Goats Pop.', numeric: true, naIfNull: true },
  { key: 'buffalo_pop', header: 'Buffaloes Pop.', numeric: true, naIfNull: true },
  { key: 'pig_pop', header: 'Pigs Pop.', numeric: true, naIfNull: true },
  { key: 'distinct_epiunits', header: 'Distinct Epiunits', numeric: true },
  { key: 'total_visits', header: 'N. of visits', numeric: true },
];

const CYCLE_CLINICAL_COLS: CycleCol[] = [
  { key: 'country', header: 'Country' },
  { key: 'district_province', header: 'Province/District' },
  { key: 'risk', header: 'Risk' },
  { key: 'cattle_exam', header: 'Cattle Exam', numeric: true },
  { key: 'cattle_pos_fmd', header: 'Cattle +FMD', numeric: true },
  { key: 'cattle_pos_lsd', header: 'Cattle +LSD', numeric: true },
  { key: 'sheep_exam', header: 'Sheep Exam', numeric: true },
  { key: 'sheep_pos_fmd', header: 'Sheep +FMD', numeric: true },
  { key: 'sheep_pos_sgp', header: 'Sheep +SGP', numeric: true },
  { key: 'sheep_pos_ppr', header: 'Sheep +PPR', numeric: true },
  { key: 'goat_exam', header: 'Goat Exam', numeric: true },
  { key: 'buffalo_exam', header: 'Buffalo Exam', numeric: true },
];

const CYCLE_SEROLOGY_COLS: CycleCol[] = [
  { key: 'country', header: 'Country' },
  { key: 'district_province', header: 'Province/District' },
  { key: 'cattle_sample', header: 'Cattle Sample', numeric: true },
  { key: 'cattle_sero_fmd', header: 'Cattle +FMD', numeric: true },
  { key: 'sheep_sample', header: 'Sheep Sample', numeric: true },
  { key: 'sheep_sero_fmd', header: 'Sheep +FMD', numeric: true },
  { key: 'goat_sample', header: 'Goat Sample', numeric: true },
  { key: 'pig_sample', header: 'Pig Sample', numeric: true },
  { key: 'buffalo_sample', header: 'Buffalo Sample', numeric: true },
  { key: 'wild_sample', header: 'Wild Sample', numeric: true },
];

const cycleCellValue = (row: any, col: CycleCol) => {
  const v = row[col.key];
  if (col.key === 'risk') return v || '—';
  if (col.naIfNull && (v === null || v === undefined)) return 'N/A';
  if (col.numeric) return v ?? 0;
  return v ?? '';
};

const cycleRowsForExcel = (rows: any[], cols: CycleCol[]) =>
  rows.map(row => {
    const out: Record<string, string | number> = {};
    cols.forEach(col => {
      const v = row[col.key];
      if (col.key === 'risk') {
        out[col.header] = row.risk || '';
      } else if (col.naIfNull && (v === null || v === undefined)) {
        out[col.header] = 'N/A';
      } else if (col.numeric) {
        out[col.header] = Number(v ?? 0);
      } else {
        out[col.header] = v ?? '';
      }
    });
    return out;
  });

const cyclePopulationTitle = (
  quarter: number,
  reportYear: number,
  inventoryYear?: number | null,
  isFallback?: boolean,
) => {
  const base = `Animal Population - Quarter ${quarter}, ${reportYear}`;
  if (inventoryYear == null) return `${base} (no inventory available)`;
  if (isFallback) return `${base} — population from inventory ${inventoryYear}`;
  return `${base} — inventory ${inventoryYear}`;
};


interface LegendItem {
  label: string;
  color: string;
}

// Custom legend column rendered beside the Plotly chart (Plotly's own legend is disabled).
const ChartLegend: React.FC<{ items: LegendItem[] }> = ({ items }) => (
  <div className="flex flex-col gap-2 pl-3 pt-2" style={{ width: 150, flex: '0 0 150px' }}>
    {items.map((it) => (
      <div key={it.label} className="flex items-center gap-2 text-sm text-gray-700">
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            borderRadius: 3,
            backgroundColor: it.color,
            flex: '0 0 14px',
          }}
        />
        <span>{it.label}</span>
      </div>
    ))}
  </div>
);

const FREEDOM_LEGEND: LegendItem[] = [
  { label: 'P(free)', color: '#7fc97f' },
  { label: 'Outbreak month', color: '#dc2626' },
  { label: 'Sensitivity (FSSe)', color: '#80b1d3' },
  { label: 'P(Intro)', color: '#f16913' },
  { label: 'Serology', color: '#fdc086' },
  { label: 'Clinical', color: '#beaed4' },
];

const EARLY_LEGEND: LegendItem[] = [
  { label: 'EDSSe', color: '#c59d00' },
  { label: 'P(Intro)', color: '#f16913' },
  { label: 'Serology', color: '#fdc086' },
  { label: 'Clinical', color: '#beaed4' },
];

const Thrace: React.FC = () => {
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dataValidationErrors, setDataValidationErrors] = useState<ErrorRow[]>([]);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showReportSection, setShowReportSection] = useState(false);
  const [showMapSection, setShowMapSection] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [inventoryNationId, setInventoryNationId] = useState<number>(300);
  const [inventoryYear, setInventoryYear] = useState<number>(new Date().getFullYear());
  const [inventoryDownloading, setInventoryDownloading] = useState(false);
  const [inventoryUploading, setInventoryUploading] = useState(false);
  const [inventoryUploadMessage, setInventoryUploadMessage] = useState('');
  const inventoryFileRef = useRef<HTMLInputElement>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState<CycleReportData | null>(null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState<Date | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<number>(300); // Default to Greece
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState<number>(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showFreedomSection, setShowFreedomSection] = useState(false);
  const [freedomLoading, setFreedomLoading] = useState(false);
  const [freedomError, setFreedomError] = useState<string | null>(null);
  const [freedomData, setFreedomData] = useState<any | null>(null);
  const [freedomDisease, setFreedomDisease] = useState('FMD');
  // Corrected model is per-country: GR / BG / TK only (no ALL).
  // Species is always ALL (issue #34) — no species picker.
  const [freedomRegion, setFreedomRegion] = useState('GR');
  const freedomChartRef = useRef<HTMLDivElement | null>(null);

  // Early detection (EDSSe) — separate section, own filters, uses the edsse parameters
  const [showEarlySection, setShowEarlySection] = useState(false);
  const [earlyLoading, setEarlyLoading] = useState(false);
  const [earlyError, setEarlyError] = useState<string | null>(null);
  const [earlyData, setEarlyData] = useState<any | null>(null);
  const [earlyDisease, setEarlyDisease] = useState('FMD');
  const [earlyRegion, setEarlyRegion] = useState('GR');
  const earlyChartRef = useRef<HTMLDivElement | null>(null);

  // Simple product metadata view/download
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [metadataText, setMetadataText] = useState('');
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  const templates: Template[] = [
    { id: 'bulgaria', name: 'Bulgaria', fileName: 'ThraceActivitiesBulgaria' },
    { id: 'greece', name: 'Greece', fileName: 'ThraceActivitiesGreece' },
    { id: 'turkey', name: 'Türkiye', fileName: 'ThraceActivitiesTurkey' },
  ];

  const handleDownloadTemplate = (template: Template) => {
    // Download the Excel file from the static folder
    const fileUrl = `/templates/${template.fileName}.xlsx`;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = `${template.fileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowTemplateModal(false);
  };

  const clearUploadState = () => {
    setUploadErrors([]);
    setUploadMessage('');
    setUploadSuccess(false);
    setDataValidationErrors([]);
    setShowErrorDetails(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    clearUploadState();
    setShowUploadModal(true);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx')) {
      setUploadErrors(['Only .xlsx files are allowed']);
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      setUploadErrors(['You must be logged in to upload data. Please log in and try again.']);
      return;
    }

    console.log('Token found, uploading file...', { tokenLength: token.length, fileName: file.name });

    setUploadLoading(true);
    setUploadErrors([]);
    setUploadMessage('');
    setUploadSuccess(false);
    setDataValidationErrors([]);
    setShowErrorDetails(false);

    try {
      console.log('Creating FormData...');
      const response = await apiService.thrace.uploadData(file);
      console.log('Upload response:', response.data);

      if (response.data.success) {
        // Validation passed and rows were imported directly into factivities.
        setUploadSuccess(true);
        const invWarn = response.data.inventory_warnings || [];
        let msg = `✓ ${response.data.message || `Imported ${response.data.inserted_count} rows.`}`;
        if (invWarn.length) {
          msg = `✓ Imported ${response.data.inserted_count} rows.\n⚠ ${invWarn.join('\n⚠ ')}`;
        }
        setUploadMessage(msg);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setTimeout(() => {
          setShowUploadModal(false);
          setDataValidationErrors([]);
          setShowErrorDetails(false);
        }, invWarn.length ? 8000 : 3000);
      } else if (response.data.has_errors) {
        // Validation failed - nothing imported. Show the error report to fix & re-upload.
        const details = response.data.error_rows_detail || [];
        setDataValidationErrors(details);
        setShowErrorDetails(true);
        setUploadMessage(
          response.data.message ||
          `⚠️ ${response.data.error_count} of ${response.data.total_rows} rows have validation errors. ` +
          `Nothing was imported. Fix the rows below and re-upload.`
        );
        // Also surface specific row errors in the main error list (not only the expandable table)
        setUploadErrors(
          details.length > 0
            ? details.map((e: any) => `Row ${e.rowId}: ${e.error}`)
            : [response.data.message || 'Validation failed']
        );
        setUploadSuccess(false);
      } else {
        setUploadErrors([response.data.message || 'No valid data rows found in file']);
        setUploadSuccess(false);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      if (error.response?.status === 401) {
        setUploadErrors(['Authentication failed. Your session may have expired.']);
      } else if (error.response?.data?.detail) {
        setUploadErrors([error.response.data.detail]);
      } else if (error.response?.data?.errors) {
        // Handle array of error objects and extract messages
        const errorMessages = Array.isArray(error.response.data.errors)
          ? error.response.data.errors.map((err: any) => {
              if (typeof err === 'string') return err;
              if (err.msg) return err.msg;
              return JSON.stringify(err);
            })
          : [error.response.data.errors];
        setUploadErrors(errorMessages);
      } else if (error.message) {
        setUploadErrors([error.message]);
      } else {
        setUploadErrors(['An error occurred during file upload']);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const handleReportClick = () => {
    setShowReportSection(true);
    setShowFreedomSection(false);
    setShowEarlySection(false);
    setShowMapSection(false);
    setReportData(null);
  };

  const handleMapClick = () => {
    setShowMapSection(true);
    setShowReportSection(false);
    setShowFreedomSection(false);
    setShowEarlySection(false);
  };

  const handleInventoryClick = () => {
    setShowInventoryModal(true);
  };

  const handleDownloadInventoryTemplate = async () => {
    setInventoryDownloading(true);
    setInventoryUploadMessage('');
    try {
      const res = await apiService.thrace.downloadInventoryTemplate(
        inventoryNationId,
        inventoryYear
      );
      const disposition = res.headers?.['content-disposition'] as string | undefined;
      const match = disposition?.match(/filename="?([^"]+)"?/i);
      const filename =
        match?.[1] || `ThraceInventory_${inventoryNationId}_${inventoryYear}.xlsx`;
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.response?.data?.detail || err?.message || 'Inventory template download failed');
    } finally {
      setInventoryDownloading(false);
    }
  };

  const handleInventoryFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      setInventoryUploadMessage('Only .xlsx files are allowed');
      return;
    }
    setInventoryUploading(true);
    setInventoryUploadMessage('Uploading…');
    try {
      const res = await apiService.thrace.uploadInventory(file);
      const data = res.data;
      if (data?.has_errors) {
        const errs = (data.errors || []).slice(0, 8).join('\n');
        setInventoryUploadMessage(
          `${data.message || 'Upload rejected'}\n${errs}${
            (data.errors || []).length > 8 ? '\n…' : ''
          }`
        );
      } else {
        setInventoryUploadMessage(data?.message || 'Inventory saved.');
      }
    } catch (err: any) {
      setInventoryUploadMessage(
        err?.response?.data?.detail || err?.message || 'Inventory upload failed'
      );
    } finally {
      setInventoryUploading(false);
      if (inventoryFileRef.current) inventoryFileRef.current.value = '';
    }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportData(null);
    setReportGeneratedAt(null);

    try {
      const response = await apiService.thrace.getCycleReport(selectedCountry, selectedYear, selectedQuarter);
      console.log('Cycle report response:', response.data);
      
      if (response.data.success) {
        setReportData({
          ...response.data.data,
          population_inventory_year: response.data.population_inventory_year ?? null,
          population_inventory_is_fallback: !!response.data.population_inventory_is_fallback,
          outbreak: response.data.outbreak ?? null,
        });
        setReportGeneratedAt(new Date());
      }
    } catch (error: any) {
      console.error('Report generation error:', error);
      alert(`Error generating report: ${error.response?.data?.detail || error.message}`);
    } finally {
      setReportLoading(false);
    }
  };

  const formatReportGeneratedAt = (d: Date) =>
    d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleFreedomClick = () => {
    setShowFreedomSection(true);
    setShowReportSection(false);
    setShowEarlySection(false);
    setShowMapSection(false);
    // Don't auto-fetch - let user set filters first
  };

  const handleEarlyClick = () => {
    setShowEarlySection(true);
    setShowFreedomSection(false);
    setShowReportSection(false);
    setShowMapSection(false);
  };

  const handleMetadataClick = async () => {
    setShowMetadataModal(true);
    setMetadataError(null);
    if (metadataText) return;
    setMetadataLoading(true);
    try {
      const res = await apiService.thrace.getMetadata();
      setMetadataText(typeof res.data === 'string' ? res.data : String(res.data));
    } catch (err: any) {
      setMetadataError(err?.response?.data?.detail || err?.message || 'Error loading metadata');
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleDownloadMetadata = () => {
    const blob = new Blob([metadataText], { type: 'application/x-yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'thrace_metadata.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fetchFreedomData = async () => {
    setFreedomLoading(true);
    setFreedomError(null);
    setFreedomData(null);
    try {
      const res = await apiService.thrace.getFreedomData(
        'ALL',
        freedomDisease,
        freedomRegion
      );
      const payload = res.data?.data;
      if (!payload?.labels?.length) {
        setFreedomError('No surveillance data found for these filters.');
        return;
      }
      setFreedomData(payload);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Error loading freedom analysis';
      setFreedomError(message);
    } finally {
      setFreedomLoading(false);
    }
  };

  const fetchEarlyData = async () => {
    setEarlyLoading(true);
    setEarlyError(null);
    setEarlyData(null);
    try {
      const res = await apiService.thrace.getFreedomData(
        'ALL',
        earlyDisease,
        earlyRegion
      );
      const payload = res.data?.data;
      if (!payload?.labels?.length) {
        setEarlyError('No surveillance data found for these filters.');
        return;
      }
      setEarlyData(payload);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Error loading early detection analysis';
      setEarlyError(message);
    } finally {
      setEarlyLoading(false);
    }
  };

  const lastSeriesValue = (series: any[] | undefined) => {
    if (!series?.length) return 0;
    const nums = series.map((v) => Number(v)).filter((v) => !Number.isNaN(v));
    return nums.length ? nums[nums.length - 1] : 0;
  };

  useEffect(() => {
    if (!freedomData || !freedomChartRef.current) return;

    const labels: string[] = freedomData.labels || [];
    if (!labels.length) return;

    const pfree = (freedomData.pfree || []).map((v: any) => Number(v));
    const sens = (freedomData.sens || []).map((v: any) => Number(v));
    const pintro = (freedomData.pintro || []).map((v: any) => Number(v));
    const sero = freedomData.sero || [];
    const clin = freedomData.clin || [];
    const outbreakFlags: boolean[] = (freedomData.outbreak || []).map((v: any) => Boolean(v));
    const outbreakSources: (string | null)[] = freedomData.outbreak_source || [];
    const hasOutbreak = outbreakFlags.some(Boolean);
    const outbreakPfree = labels.map((lab, i) => (outbreakFlags[i] ? pfree[i] : null));
    const outbreakHover = labels.map((_, i) => {
      if (!outbreakFlags[i]) return '';
      const src = outbreakSources[i] || 'outbreak';
      return `Outbreak month (${src}) — P(free)=0`;
    });

    // Plotly traces matching PHP app layout
    const traces: any[] = [
      // Row 1 (bottom): Stacked bars for animals tested
      {
        name: 'Serology',
        type: 'bar',
        x: labels,
        y: sero,
        yaxis: 'y',
        marker: { color: '#fdc086' }
      },
      {
        name: 'Clinical',
        type: 'bar',
        x: labels,
        y: clin,
        yaxis: 'y',
        marker: { color: '#beaed4' }
      },
      // Row 2: P(introduction)
      {
        name: 'P(Intro)',
        type: 'scatter',
        x: labels,
        y: pintro,
        fill: 'tozeroy',
        yaxis: 'y2',
        line: { shape: 'spline', smoothing: 0.8, color: '#f16913' },
        marker: { color: '#f16913' }
      },
      // Row 3: Surveillance sensitivity (FSSe)
      {
        name: 'Sensitivity (FSSe)',
        type: 'scatter',
        x: labels,
        y: sens,
        fill: 'tozeroy',
        yaxis: 'y3',
        line: { shape: 'spline', smoothing: 0.8, color: '#80b1d3' },
        marker: { color: '#80b1d3' }
      },
      // Row 4 (top): P(free)
      {
        name: 'P(free)',
        type: 'scatter',
        x: labels,
        y: pfree,
        fill: 'tozeroy',
        yaxis: 'y4',
        line: { shape: 'spline', smoothing: 0.8 },
        marker: { color: '#7fc97f' }
      },
    ];

    if (hasOutbreak) {
      traces.push({
        name: 'Outbreak month',
        type: 'scatter',
        mode: 'markers',
        x: labels,
        y: outbreakPfree,
        yaxis: 'y4',
        marker: {
          color: '#dc2626',
          size: 11,
          symbol: 'diamond',
          line: { color: '#7f1d1d', width: 1 },
        },
        text: outbreakHover,
        hovertemplate: '%{text}<extra></extra>',
      });
    }

    const layout = {
      barmode: 'stack',
      hovermode: 'closest',
      margin: { t: 20, b: 50, l: 80, r: 20 },
      showlegend: false,
      xaxis: {
        title: 'Month',
        type: 'date',
        showspikes: true
      },
      yaxis: {
        domain: [0, 0.24],
        title: 'Animals<br>tested',
        zeroline: false,
        autorange: true
      },
      yaxis2: {
        domain: [0.26, 0.48],
        title: 'Probability<br>of introduction',
        zeroline: false
      },
      yaxis3: {
        domain: [0.52, 0.74],
        title: 'Surveillance<br>sensitivity',
        zeroline: false
      },
      yaxis4: {
        domain: [0.76, 1],
        title: 'Probability<br>of freedom',
        zeroline: false,
        // Outbreak months force P(free)=0 — open the axis so the drop is visible
        range: hasOutbreak ? [0, 1] : [0.9, 1]
      }
    };

    const config = { responsive: true };

    const freedomEl = freedomChartRef.current;
    Plotly.purge(freedomEl);
    Plotly.newPlot(freedomEl, traces, layout, config);

    return () => {
      if (freedomEl) {
        Plotly.purge(freedomEl);
      }
    };
  }, [freedomData]);

  // Early detection (EDSSe) chart: EDSSe + P(intro) + animals tested
  useEffect(() => {
    if (!earlyData || !earlyChartRef.current) return;

    const labels: string[] = earlyData.labels || [];
    if (!labels.length) return;

    const edsse = (earlyData.edsse || []).map((v: any) => (v === null || v === undefined ? null : Number(v)));
    const pintro = (earlyData.pintro || []).map((v: any) => Number(v));
    const sero = earlyData.sero || [];
    const clin = earlyData.clin || [];

    const traces: any[] = [
      // Row 1 (bottom): animals tested
      {
        name: 'Serology',
        type: 'bar',
        x: labels,
        y: sero,
        yaxis: 'y',
        marker: { color: '#fdc086' }
      },
      {
        name: 'Clinical',
        type: 'bar',
        x: labels,
        y: clin,
        yaxis: 'y',
        marker: { color: '#beaed4' }
      },
      // Row 2: P(introduction)
      {
        name: 'P(Intro)',
        type: 'scatter',
        x: labels,
        y: pintro,
        fill: 'tozeroy',
        yaxis: 'y2',
        line: { shape: 'spline', smoothing: 0.8, color: '#f16913' },
        marker: { color: '#f16913' }
      },
      // Row 3 (top): EDSSe
      {
        name: 'EDSSe',
        type: 'scatter',
        x: labels,
        y: edsse,
        fill: 'tozeroy',
        yaxis: 'y3',
        connectgaps: false,
        line: { shape: 'spline', smoothing: 0.8, color: '#c59d00' },
        marker: { color: '#c59d00' }
      }
    ];

    const layout = {
      barmode: 'stack',
      hovermode: 'closest',
      margin: { t: 20, b: 50, l: 80, r: 20 },
      showlegend: false,
      xaxis: {
        title: 'Month',
        type: 'date',
        showspikes: true
      },
      yaxis: {
        domain: [0, 0.31],
        title: 'Animals<br>tested',
        zeroline: false,
        autorange: true
      },
      yaxis2: {
        domain: [0.35, 0.64],
        title: 'Probability<br>of introduction',
        zeroline: false
      },
      yaxis3: {
        domain: [0.68, 1],
        title: 'Early detection<br>sensitivity',
        zeroline: false
      }
    };

    const config = { responsive: true };

    const earlyEl = earlyChartRef.current;
    Plotly.purge(earlyEl);
    Plotly.newPlot(earlyEl, traces, layout, config);

    return () => {
      if (earlyEl) {
        Plotly.purge(earlyEl);
      }
    };
  }, [earlyData]);

  const handleDownloadExcel = () => {
    if (!reportData) return;

    const wb = XLSX.utils.book_new();
    const ws: any = {};
    const merges: Array<{ s: { r: number; c: number }; e: { r: number; c: number } }> = [];
    let currentRow = 0;
    let maxCols = 0;

    // Match on-screen Tailwind blues / reds / greens
    const thinBorder = {
      top: { style: 'thin', color: { rgb: 'D1D5DB' } },
      bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
      left: { style: 'thin', color: { rgb: 'D1D5DB' } },
      right: { style: 'thin', color: { rgb: 'D1D5DB' } },
    };

    const addSection = (
      title: string,
      data: any[],
      colors: { banner: string; header: string; zebra: string },
      numericFields: string[],
      areaKey: string = 'Province/District',
    ) => {
      if (!data.length) return;

      const totals: any = { Country: '', [areaKey]: 'TOT' };
      numericFields.forEach(field => {
        const nums = data
          .map(row => row[field])
          .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
        totals[field] = nums.length > 0 ? nums.reduce((sum, n) => sum + n, 0) : 'N/A';
      });
      const sectionRows = [...data, totals];
      const headers = Object.keys(sectionRows[0]);
      maxCols = Math.max(maxCols, headers.length);
      const lastCol = headers.length - 1;

      // Section banner (matches on-screen h3 strip)
      merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: lastCol } });
      for (let C = 0; C <= lastCol; C++) {
        ws[XLSX.utils.encode_cell({ r: currentRow, c: C })] = {
          t: 's',
          v: C === 0 ? title : '',
          s: {
            fill: { patternType: 'solid', fgColor: { rgb: colors.banner } },
            font: { bold: true, sz: 12, color: { rgb: '374151' } },
            alignment: { horizontal: 'left', vertical: 'center' },
            border: thinBorder,
          },
        };
      }
      currentRow += 1;

      // Column headers
      for (let C = 0; C < headers.length; C++) {
        ws[XLSX.utils.encode_cell({ r: currentRow, c: C })] = {
          t: 's',
          v: headers[C],
          s: {
            fill: { patternType: 'solid', fgColor: { rgb: colors.header } },
            font: { bold: true, sz: 10, color: { rgb: '374151' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: thinBorder,
          },
        };
      }
      currentRow += 1;

      const textCols = new Set(
        ['Country', 'Province/District', 'District', 'Risk'].map(h => headers.indexOf(h)).filter(i => i >= 0),
      );

      for (let R = 0; R < sectionRows.length; R++) {
        const isTotal = R === sectionRows.length - 1;
        const zebra = !isTotal && R % 2 === 1;
        const fillRgb = isTotal ? 'FEF08A' : zebra ? colors.zebra : 'FFFFFF';

        for (let C = 0; C < headers.length; C++) {
          const cellValue = sectionRows[R][headers[C]] ?? '';
          const isNum = typeof cellValue === 'number';
          ws[XLSX.utils.encode_cell({ r: currentRow, c: C })] = {
            t: isNum ? 'n' : 's',
            v: cellValue,
            s: {
              fill: { patternType: 'solid', fgColor: { rgb: fillRgb } },
              font: { bold: isTotal, sz: 10, color: { rgb: '1F2937' } },
              alignment: {
                horizontal: textCols.has(C) || headers[C] === 'Quarter' || headers[C] === 'Year' ? 'left' : 'right',
                vertical: 'center',
              },
              border: thinBorder,
            },
          };
        }
        currentRow += 1;
      }

      currentRow += 2; // gap between sections
    };

    const countryName =
      reportData.population[0]?.country ||
      reportData.clinical[0]?.country ||
      reportData.serology[0]?.country ||
      '';

    // Report title + generation date (R26)
    const titleText = `THRACE Cycle Report — ${countryName} — Quarter ${selectedQuarter}, ${selectedYear}`;
    ws[XLSX.utils.encode_cell({ r: 0, c: 0 })] = {
      t: 's',
      v: titleText,
      s: {
        font: { bold: true, sz: 14, color: { rgb: '111827' } },
        alignment: { horizontal: 'left', vertical: 'center' },
      },
    };
    const generatedLabel = reportGeneratedAt
      ? `Generated: ${formatReportGeneratedAt(reportGeneratedAt)}`
      : `Generated: ${formatReportGeneratedAt(new Date())}`;
    ws[XLSX.utils.encode_cell({ r: 1, c: 0 })] = {
      t: 's',
      v: generatedLabel,
      s: {
        font: { italic: true, sz: 10, color: { rgb: '4B5563' } },
        alignment: { horizontal: 'left', vertical: 'center' },
      },
    };
    currentRow = 3;
    if (reportData.outbreak?.has_outbreak) {
      const parts = Object.entries(reportData.outbreak.by_disease || {}).map(
        ([dz, entries]) =>
          `${dz}: ${(entries || []).map((e) => `${e.label} (${e.source})`).join(', ')}`
      );
      const outbreakLine = `OUTBREAK MONTH(S) — P(free)=0 for affected diseases: ${parts.join('; ')}`;
      ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = {
        t: 's',
        v: outbreakLine,
        s: {
          font: { bold: true, sz: 10, color: { rgb: '991B1B' } },
          fill: { patternType: 'solid', fgColor: { rgb: 'FEE2E2' } },
          alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
        },
      };
      currentRow += 2;
    }

    const popData = cycleRowsForExcel(reportData.population, CYCLE_POP_COLS);
    addSection(
      cyclePopulationTitle(
        selectedQuarter,
        selectedYear,
        reportData.population_inventory_year,
        reportData.population_inventory_is_fallback,
      ),
      popData,
      { banner: 'DBEAFE', header: 'EFF6FF', zebra: 'F8FAFC' },
      CYCLE_POP_COLS.filter(c => c.numeric).map(c => c.header),
    );

    const clinicalData = cycleRowsForExcel(reportData.clinical, CYCLE_CLINICAL_COLS);
    addSection(
      `Clinical Examination - Quarter ${selectedQuarter}, ${selectedYear}`,
      clinicalData,
      { banner: 'FEE2E2', header: 'FEF2F2', zebra: 'FFF7F7' },
      CYCLE_CLINICAL_COLS.filter(c => c.numeric).map(c => c.header),
    );

    const serologyData = cycleRowsForExcel(reportData.serology, CYCLE_SEROLOGY_COLS);
    addSection(
      `Serological Examination - Quarter ${selectedQuarter}, ${selectedYear}`,
      serologyData,
      { banner: 'DCFCE7', header: 'F0FDF4', zebra: 'F7FEF9' },
      CYCLE_SEROLOGY_COLS.filter(c => c.numeric).map(c => c.header),
    );

    // Merge title + generation date across columns
    if (maxCols > 1) {
      merges.unshift({ s: { r: 1, c: 0 }, e: { r: 1, c: maxCols - 1 } });
      merges.unshift({ s: { r: 0, c: 0 }, e: { r: 0, c: maxCols - 1 } });
    }
    ws['!merges'] = merges;
    ws['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: Math.max(currentRow - 1, 0), c: Math.max(maxCols - 1, 0) },
    });
    ws['!cols'] = Array.from({ length: maxCols }, (_, i) => ({
      wch: i === 0 ? 12 : i === 1 ? 18 : i === 2 ? 14 : 14,
    }));
    ws['!rows'] = [{ hpt: 22 }];

    XLSX.utils.book_append_sheet(wb, ws, `Cycle Report Q${selectedQuarter}`);
    XLSX.writeFile(wb, `CycleReport_Q${selectedQuarter}_${selectedYear}.xlsx`);
  };

  return (
    <div className="container mx-auto px-4">
      {/* Header Section */}
      <section className="mb-6">
        <p className="font-black capitalize text-2xl mb-6 font-martaBold">
          THRACE
        </p>
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <p className="text-gray-700 mb-4">
              <strong>THRACE:</strong> An international Transboundary High Risk Area Coordinated Epidemio-surveillance supported by EuFMD.
            </p>
            <p className="text-gray-700 mb-4">
              The THRACE project involves Türkiye, Bulgaria and Greece who share common borders in the Thrace area.
            </p>
            <p className="text-gray-700">
              This database has been developed to facilitate the regular submission and management of surveillance data generated under the THRACE program from the three countries.
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-start space-x-2">
          <button 
            className="nav-btn"
            onClick={() => setShowTemplateModal(true)}
          >
            Template Download
          </button>
          <button 
            className="nav-btn"
            onClick={handleUploadClick}
          >
            Data Upload
          </button>
          <button 
            className="nav-btn"
            onClick={handleReportClick}
          >
            Report
          </button>
          <button 
            className="nav-btn"
            onClick={handleFreedomClick}
          >
            Freedom from disease analysis
          </button>
          <button 
            className="nav-btn"
            onClick={handleEarlyClick}
          >
            Early detection
          </button>
          <button 
            className="nav-btn"
            onClick={handleMapClick}
          >
            Map
          </button>
          <button
            className="nav-btn"
            onClick={handleInventoryClick}
          >
            Inventory
          </button>
          <button 
            className="nav-btn"
            onClick={handleMetadataClick}
          >
            About / Metadata
          </button>
        </div>
      </section>

      {/* Report Section */}
      {showReportSection && (
        <section className="mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Cycle Report Generator</h2>
            
            {/* Selectors */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value={300}>Greece</option>
                  <option value={100}>Bulgaria</option>
                  <option value={792}>Türkiye</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quarter
                </label>
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value={1}>Q1 (Jan-Mar)</option>
                  <option value={2}>Q2 (Apr-Jun)</option>
                  <option value={3}>Q3 (Jul-Sep)</option>
                  <option value={4}>Q4 (Oct-Dec)</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleGenerateReport}
                  disabled={reportLoading}
                  className="w-full nav-btn disabled:opacity-50"
                >
                  {reportLoading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
            </div>

            {/* Report Data Tables */}
            {reportData && (
              <div className="space-y-6">
                {reportGeneratedAt && (
                  <p className="text-sm text-gray-600 -mt-2">
                    Generated: <span className="font-medium text-gray-800">{formatReportGeneratedAt(reportGeneratedAt)}</span>
                  </p>
                )}
                {reportData.outbreak?.has_outbreak && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">
                    <strong>Outbreak month(s)</strong>
                    {reportData.outbreak.note ? ` — ${reportData.outbreak.note}` : ''}
                    <ul className="mt-2 list-disc list-inside">
                      {Object.entries(reportData.outbreak.by_disease || {}).map(([dz, entries]) => (
                        <li key={dz}>
                          {dz}:{' '}
                          {(entries || [])
                            .map((e) => `${e.label}${e.source ? ` (${e.source})` : ''}`)
                            .join(', ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Section 1: Animal Population */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 bg-blue-100 px-4 py-2 rounded">
                    {cyclePopulationTitle(
                      selectedQuarter,
                      selectedYear,
                      reportData.population_inventory_year,
                      reportData.population_inventory_is_fallback,
                    )}
                  </h3>
                  {reportData.population_inventory_is_fallback && reportData.population_inventory_year != null && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2 mb-3">
                      No inventory for {selectedYear}; showing latest available inventory ({reportData.population_inventory_year}).
                      Upload {selectedYear} inventory when ready.
                    </p>
                  )}
                  {reportData.population_inventory_year == null && (
                    <p className="text-sm text-gray-600 mb-3">
                      No inventory on file for this country — population columns show N/A.
                    </p>
                  )}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border">
                      <thead className="bg-blue-50">
                        <tr>
                          {CYCLE_POP_COLS.map(col => (
                            <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                              {col.header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportData.population.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            {CYCLE_POP_COLS.map(col => (
                              <td
                                key={col.key}
                                className={`px-3 py-2 text-sm${col.numeric ? ' text-right' : ''}`}
                              >
                                {cycleCellValue(row, col)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Section 2: Clinical Examination */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 bg-red-100 px-4 py-2 rounded">
                    Clinical Examination - Quarter {selectedQuarter}, {selectedYear}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border">
                      <thead className="bg-red-50">
                        <tr>
                          {CYCLE_CLINICAL_COLS.map(col => (
                            <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                              {col.header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportData.clinical.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            {CYCLE_CLINICAL_COLS.map(col => (
                              <td
                                key={col.key}
                                className={`px-3 py-2 text-sm${col.numeric ? ' text-right' : ''}`}
                              >
                                {cycleCellValue(row, col)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Section 3: Serological Examination */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-700 bg-green-100 px-4 py-2 rounded">
                    Serological Examination - Quarter {selectedQuarter}, {selectedYear}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border">
                      <thead className="bg-green-50">
                        <tr>
                          {CYCLE_SEROLOGY_COLS.map(col => (
                            <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                              {col.header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportData.serology.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            {CYCLE_SEROLOGY_COLS.map(col => (
                              <td
                                key={col.key}
                                className={`px-3 py-2 text-sm${col.numeric ? ' text-right' : ''}`}
                              >
                                {cycleCellValue(row, col)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Download Excel Button */}
                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleDownloadExcel}
                    className="nav-btn px-8"
                  >
                    📥 Download Excel Report
                  </button>
                </div>
              </div>
            )}

            {!reportData && !reportLoading && (
              <div className="text-center py-8 text-gray-500">
                Select country, year, and quarter, then click "Generate Report" to view the data.
              </div>
            )}
          </div>
        </section>
      )}

      {showFreedomSection && (
        <section className="mb-6">
          <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disease</label>
                <select
                  value={freedomDisease}
                  onChange={(e) => setFreedomDisease(e.target.value)}
                  className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="FMD">FMD</option>
                  <option value="LSD">LSD</option>
                  <option value="SGP">SGP</option>
                  <option value="PPR">PPR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                <select
                  value={freedomRegion}
                  onChange={(e) => setFreedomRegion(e.target.value)}
                  className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="BG">Bulgaria</option>
                  <option value="GR">Greece</option>
                  <option value="TK">Türkiye</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  className="nav-btn"
                  onClick={() => fetchFreedomData()}
                  disabled={freedomLoading}
                >
                  {freedomLoading ? 'Loading...' : 'Load analysis'}
                </button>
              </div>
            </div>

            {freedomError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                {freedomError}
              </div>
            )}

            {freedomData ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <span>{freedomData.labels.length} months (all years)</span>
                  <span>P(free) last: {lastSeriesValue(freedomData.pfree).toFixed(3)}</span>
                  <span>FSSe last: {lastSeriesValue(freedomData.sens).toFixed(3)}</span>
                  <span>P(introduction) last: {lastSeriesValue(freedomData.pintro).toFixed(3)}</span>
                </div>
                {Array.isArray(freedomData.outbreak_months) && freedomData.outbreak_months.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">
                    <strong>Outbreak month(s)</strong> — P(free) set to 0
                    (visit positives and/or WAHIS/manual flag):{' '}
                    {freedomData.outbreak_months
                      .map((m: string) => {
                        const src =
                          (freedomData.outbreak_source || [])[
                            (freedomData.labels || []).indexOf(m)
                          ] || '';
                        return src ? `${m} (${src})` : m;
                      })
                      .join(', ')}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <div ref={freedomChartRef} style={{ flex: 1, minWidth: 0, height: '80vh' }} />
                  <ChartLegend items={FREEDOM_LEGEND} />
                </div>
              </div>
            ) : (
              !freedomLoading && (
                <div className="text-gray-500">Select filters and load the analysis.</div>
              )
            )}
          </div>
        </section>
      )}

      {/* Early Detection Section */}
      {showEarlySection && (
        <section className="mb-6">
          <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
            <h2 className="text-xl font-bold">Early detection surveillance sensitivity (EDSSe)</h2>
            <p className="text-sm text-gray-600">
              Uses the early-detection design parameters (design prevalence, temporal coverage and
              risk weighting), which differ from the freedom-from-disease analysis.
            </p>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disease</label>
                <select
                  value={earlyDisease}
                  onChange={(e) => setEarlyDisease(e.target.value)}
                  className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="FMD">FMD</option>
                  <option value="LSD">LSD</option>
                  <option value="SGP">SGP</option>
                  <option value="PPR">PPR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                <select
                  value={earlyRegion}
                  onChange={(e) => setEarlyRegion(e.target.value)}
                  className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="BG">Bulgaria</option>
                  <option value="GR">Greece</option>
                  <option value="TK">Türkiye</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  className="nav-btn"
                  onClick={() => fetchEarlyData()}
                  disabled={earlyLoading}
                >
                  {earlyLoading ? 'Loading...' : 'Load analysis'}
                </button>
              </div>
            </div>

            {earlyError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                {earlyError}
              </div>
            )}

            {earlyData ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <span>{earlyData.labels.length} months (all years)</span>
                  <span>EDSSe last: {lastSeriesValue(earlyData.edsse).toFixed(3)}</span>
                  <span>P(introduction) last: {lastSeriesValue(earlyData.pintro).toFixed(3)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <div ref={earlyChartRef} style={{ flex: 1, minWidth: 0, height: '70vh' }} />
                  <ChartLegend items={EARLY_LEGEND} />
                </div>
              </div>
            ) : (
              !earlyLoading && (
                <div className="text-gray-500">Select filters and load the analysis.</div>
              )
            )}
          </div>
        </section>
      )}

      {/* Map Section */}
      {showMapSection && (
        <section className="mb-6">
          <ThraceMapPanel onClose={() => setShowMapSection(false)} />
        </section>
      )}

      {/* Metadata Modal */}
      {showMetadataModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">THRACE — Product metadata</h2>
              <button
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
                onClick={() => setShowMetadataModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Simple product metadata (custodian, contact, access, extent, lineage).
            </p>

            {metadataLoading && <div className="text-gray-500">Loading…</div>}
            {metadataError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                {metadataError}
              </div>
            )}
            {!metadataLoading && !metadataError && (
              <pre className="flex-1 overflow-auto text-xs bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap">
                {metadataText}
              </pre>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                onClick={() => setShowMetadataModal(false)}
              >
                Close
              </button>
              <button
                className="nav-btn"
                onClick={handleDownloadMetadata}
                disabled={!metadataText}
              >
                Download metadata.yaml
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Select Template to Download</h2>
            <div className="space-y-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleDownloadTemplate(template)}
                  className="nav-btn w-full"
                >
                  {template.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowTemplateModal(false)}
              className="w-full mt-4 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Inventory modal — download (prefill last year) + upload */}
      {showInventoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-2">Inventory</h2>
            <p className="text-sm text-gray-600 mb-4">
              Download a template for your country and year, update the animal numbers, then upload
              the file. If this year is empty, the template starts from the last year you reported.
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={inventoryNationId}
                  onChange={(e) => setInventoryNationId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value={300}>Greece</option>
                  <option value={100}>Bulgaria</option>
                  <option value={792}>Türkiye</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="number"
                  min={2016}
                  max={2100}
                  value={inventoryYear}
                  onChange={(e) => setInventoryYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <button
              className="nav-btn w-full"
              onClick={handleDownloadInventoryTemplate}
              disabled={inventoryDownloading || inventoryUploading}
            >
              {inventoryDownloading ? 'Preparing…' : 'Download Excel'}
            </button>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload filled file (.xlsx)
              </label>
              <input
                ref={inventoryFileRef}
                type="file"
                accept=".xlsx"
                onChange={handleInventoryFileSelect}
                disabled={inventoryUploading || inventoryDownloading}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-green-50 file:text-green-700
                  hover:file:bg-green-100"
              />
            </div>
            {inventoryUploadMessage && (
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 whitespace-pre-wrap">
                {inventoryUploadMessage}
              </div>
            )}
            <button
              onClick={() => {
                setShowInventoryModal(false);
                setInventoryUploadMessage('');
              }}
              className="w-full mt-3 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Data Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Upload Surveillance Data</h2>
            
            {!uploadSuccess ? (
              <>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                  Select your filled Excel template to upload surveillance data
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Excel File (.xlsx)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileSelect}
                    disabled={uploadLoading}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-green-50 file:text-green-700
                      hover:file:bg-green-100"
                  />
                </div>

                {uploadMessage && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                    {uploadMessage}
                  </div>
                )}

                {uploadErrors.length > 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm font-semibold text-red-700 mb-2">Validation Errors:</p>
                    <ul className="text-xs text-red-600 space-y-1 max-h-64 overflow-y-auto">
                      {uploadErrors.map((error, idx) => (
                        <li key={idx} className="list-disc list-inside">
                          {typeof error === 'string' ? error : JSON.stringify(error)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {dataValidationErrors.length > 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <button
                      type="button"
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                      className="text-sm font-semibold text-yellow-700 hover:text-yellow-800 w-full text-left"
                    >
                      {showErrorDetails ? '▼' : '▶'} View {dataValidationErrors.length} Data Validation Issues
                    </button>
                    {showErrorDetails && (
                      <div className="mt-3 max-h-80 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-yellow-300">
                              <th className="text-left py-2 px-2">Village</th>
                              <th className="text-left py-2 px-2">Country</th>
                              <th className="text-left py-2 px-2">Date</th>
                              <th className="text-left py-2 px-2">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dataValidationErrors.map((errorRow, idx) => (
                              <tr key={idx} className="border-b border-yellow-200 hover:bg-yellow-100">
                                <td className="py-2 px-2">{errorRow.village}</td>
                                <td className="py-2 px-2">{errorRow.country}</td>
                                <td className="py-2 px-2">{errorRow.date}</td>
                                <td className="py-2 px-2 text-red-600 font-semibold">{errorRow.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {uploadLoading && (
                  <div className="mb-4 text-center">
                    <div className="inline-block animate-spin">
                      <div className="h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">Processing file...</p>
                  </div>
                )}
              </>
            ) : (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm font-semibold text-green-700">✓ {uploadMessage}</p>
                <p className="text-xs text-green-600 mt-2">Your data has been successfully imported and is now available in the system.</p>
              </div>
            )}

            <button
              onClick={() => {
                clearUploadState();
                setShowUploadModal(false);
              }}
              disabled={uploadLoading}
              className="w-full mt-4 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50"
            >
              {uploadSuccess ? 'Done' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Thrace;
